import { useCallback, useEffect, useState } from 'react';

import { useDebounce, useInterval } from 'react-use';
import { v4 as uuid } from 'uuid';

import { ActivityStatusType } from '@pkg/types';
import { trimDecimals } from '@pkg/utils';

import { type ActivityListEntryType } from '@app/client';
import { useSubscribeToTopic, WebsocketTopicIds } from '@app/helpers';

import { type NotificationType } from './useDaasActivity';

export const ACTIVITY_FEED_ON_MESSAGE = 'ACTIVITY_FEED_ON_MESSAGE';

// TODO: Add missing transaction types
export enum ActivityFeedEventType {
    // Created = 'created',
    // Successful = 'successful',
    // Cancelled = 'cancelled',
    Sale = 'sale',
    Mint = 'mint',
    BidEntered = 'bidEntered',
    Bid = 'bid',
    Transfer = 'transfer',
    OfferEntered = 'offerEntered',
    Listed = 'listing',
    // Approve = 'approve'
}

const EVENT_TYPES = {
    [ActivityFeedEventType.BidEntered]: ActivityStatusType.Bid,
    [ActivityFeedEventType.Bid]: ActivityStatusType.Bid,
    [ActivityFeedEventType.Transfer]: ActivityStatusType.Transfer,
    [ActivityFeedEventType.Mint]: ActivityStatusType.Mint,
    [ActivityFeedEventType.Sale]: ActivityStatusType.Sale,
    [ActivityFeedEventType.Listed]: ActivityStatusType.Listed,
    [ActivityFeedEventType.OfferEntered]: ActivityStatusType.Offer,
};

type ActivityItem = {
    event_type: ActivityFeedEventType;
    block_number: string;
    log_index: string;
    removed: null;
    transaction_value: string;
    timestamp: string; //'2023-01-19 17:25:28.786065 UTC';
    transaction_hash: string; // 'f074ae13d66ebd718fc6ad21b6927bcade1fce38';
    contract_address: string; // '0xa8ff104f2726e8740e9fd3a3d53a37df212e69d6';
    token_id: string;
    amount: string;
    from_address: string;
    to_address: string;
    name: string;
    url: string;
    mime_type: string;
};

type UseActivityFeedProps = {
    slug: string;
    // filter?: ActivityFeedEventType;
    notificationFeedEnabled: boolean;
    pauseFeed?: boolean;
};

const MAX_NOTIFICATIONS = 3;
const NOTIFICATION_TIMEOUT = 3000;

const SLOW_SPEED = 1500;
const NORMAL_SPEED = 900;
const FAST_SPEED = 200;
const LIGHT_SPEED = 1;

export const useOwnersActivity = ({ slug, notificationFeedEnabled, pauseFeed }: UseActivityFeedProps) => {
    const [feed, setFeed] = useState<ActivityListEntryType[]>([]);
    const [addingNewNotifications, setAddingNewNotifications] = useState(false);

    const [notificationCount, setNotificationCount] = useState(0);
    const [notificationFeed, setNotificationFeed] = useState<NotificationType[]>([]);

    const [transitionFeed, setTransitionFeed] = useState<ActivityListEntryType[]>([]);
    const [feedSpeed, setFeedSpeed] = useState(SLOW_SPEED);
    const [newActivityCounter, setNewActivityCounter] = useState(0);

    useDebounce(
        () => {
            if (addingNewNotifications) {
                setNotificationFeed([]);
                setAddingNewNotifications(false);
            }
        },
        NOTIFICATION_TIMEOUT,
        [notificationCount],
    );

    useInterval(
        () => {
            if (transitionFeed.length > 30) {
                if (feedSpeed !== LIGHT_SPEED) setFeedSpeed(LIGHT_SPEED);
            } else if (transitionFeed.length > 20) {
                if (feedSpeed !== FAST_SPEED) setFeedSpeed(FAST_SPEED);
            } else if (transitionFeed.length > 10) {
                if (feedSpeed !== NORMAL_SPEED) setFeedSpeed(NORMAL_SPEED);
            } else if (feedSpeed !== SLOW_SPEED) {
                setFeedSpeed(SLOW_SPEED);
            }

            setTransitionFeed((currentFeed) => {
                const activityListEntry = currentFeed.pop();
                if (activityListEntry) onNewEntry(activityListEntry);
                return currentFeed;
            });
        },
        pauseFeed ? null : feedSpeed,
    );

    const onNewEntry = useCallback(
        (entry: ActivityListEntryType) => {
            if (notificationFeedEnabled) {
                setNotificationCount((count) => count + 1);
                const formattedItem = {
                    uuid: uuid(),
                    tokenId: entry.id,
                    image: entry.image,
                    value: entry.value?.toString(),
                    transactionType: entry.status,
                    contractAddress: entry.contract_address,
                    currency: 'ETH',
                };
                setNotificationFeed((currentFeed) => [formattedItem, ...currentFeed].slice(0, MAX_NOTIFICATIONS));
                setAddingNewNotifications(true);
            }

            setFeed((currFeed) => [entry, ...currFeed]);
        },
        [notificationFeedEnabled],
    );

    useEffect(() => {
        if (!notificationFeedEnabled) {
            setNotificationFeed([]);
            setNotificationCount(0);
        }
    }, [notificationFeedEnabled]);

    const onMessage = (msj) => {
        const data: ActivityItem = JSON.parse(msj.data)?.payload;
        if (!data) return;

        const type = EVENT_TYPES[data.event_type];
        if (type) {
            const formattedData: ActivityListEntryType = {
                uuid: uuid(),
                contract_address: data.contract_address,
                date: data.timestamp,
                id: data.token_id,
                image: data.url,
                name: data.name || data.token_id,
                value: trimDecimals(parseFloat(data.transaction_value || '0') * 0.0000000000000001),
                owner_yat: '',
                twitter_handle: '',
                status: type,
                wallet_address: '',
            };

            setTransitionFeed((currentFeed) => [formattedData, ...currentFeed]);
            if (pauseFeed) setNewActivityCounter((counter) => counter + 1);
        } else {
            // eslint-disable-next-line no-console
            console.warn(`Event not found ${data.event_type}`);
        }
    };
    useSubscribeToTopic({
        topic: `Slug,${slug}`,
        id: WebsocketTopicIds.OwnersActivity,
        messageKey: ACTIVITY_FEED_ON_MESSAGE,
        onmessage: onMessage,
    });

    useEffect(() => {
        if (!pauseFeed && newActivityCounter > 0) setNewActivityCounter(0);
    }, [newActivityCounter, pauseFeed]);

    return {
        feed,
        notificationFeed: notificationFeed.reverse(),
        notificationCount,
        addingNewNotifications,
        newActivityCounter,
        transitionFeed,
    };
};
